<template>
  <div role="tablist">
    <transition-group name="list" tag="div">
      <div v-for="(group, index) of groups" :key="'group-' + group.id">
        <b-card no-body class="mb-1">
          <b-card-header
            header-tag="header"
            class="p-1"
            role="tab"
            block
            href="#"
            v-b-toggle="`accordion-${group.id}`"
          >
            <i class="material-icons chevron">arrow_drop_down</i>

            <overflow :itemId="group.id" :options="overflow_options" />

            <div class="flex-header">
              <div>
                <input
                  class="panel-title"
                  :id="`panel-title-${group.id}`"
                  v-model="group.title"
                  @blur="updateGroup(group)"
                  @keyup.enter="updateGroup(group)"
                  @click.stop="focus"
                  placeholder="Stage title"
                  type="text"
                  v-autowidth="{
                    maxWidth: '960px',
                    minWidth: '30px',
                    comfortZone: 10,
                  }"
                  v-tooltip="{ content: 'Edit', delay: 200 }"
                />

                <span class="dates" v-show="getActionGroupStartDate(group)"
                  >{{ getActionGroupStartDate(group) }}
                  <i class="material-icons">arrow_forward</i>
                  {{ getActionGroupEndDate(group) }}</span
                >
              </div>

              <div class="chart">
                <div class="progress-bar">
                  <div
                    class="progress"
                    :style="`width: ${getChartWidth(group)}%`"
                    :class="{
                      completed:
                        countCompleted(group) === group.actions.length &&
                        group.actions.length,
                    }"
                  ></div>
                </div>
                <span :class="{ completed: countCompleted(group) > 0 }">{{
                  countCompleted(group)
                }}</span
                ><span>&nbsp;of {{ group.actions.length }}</span>
              </div>
            </div>
          </b-card-header>
          <b-collapse
            :id="`accordion-${group.id}`"
            :visible="!index"
            accordion="my-accordion"
            role="tabpanel"
          >
            <b-card-body>
              <a
                class="add-subtask"
                @click="viewAction(null, group.id, group.projectId)"
              >
                <div><img src="~@/assets/icons/add.svg" />Add subtask</div>
                <div class="icons">
                  <div class="icon attachment" />
                  <div class="icon calendar" />
                  <div class="icon user" />
                </div>
              </a>

              <transition-group name="list" tag="div">
                <action-card
                  :viewAction="viewAction"
                  v-for="action of group.actions"
                  :key="`action-${index}-${action.id}`"
                  :action="action"
                />
              </transition-group>
            </b-card-body>
          </b-collapse>
        </b-card>
      </div>
    </transition-group>
  </div>
</template>

<script>
import Vue from 'vue';
import Overflow from './Overflow';
import ActionCard from './ActionCard';

export default {
  name: 'Accordion',
  props: {
    groups: Array,
    project_id: [String, Number],
  },
  data() {
    return {
      dateFormat: 'DD MMM',
      delay: { show: 700, hide: 0 },
      overflow_options: [
        { class: 'edit', label: 'Edit name', handler: this.editGroup },
        { class: 'delete', label: 'Delete stage', handler: this.deleteGroup },
      ],
    };
  },
  methods: {
    viewAction(action, action_group_id, project_id) {
      if (action) {
        this.$store.dispatch('setCurrentActionId', action.id);
        this.$store.dispatch('setCurrentAction', action);
      } else {
        this.$store.dispatch('setNewActionGroupId', action_group_id);
        this.$store.dispatch('setNewActionProjectId', project_id);
        this.$store.dispatch('setCurrentActionId', -1);
        this.$store.dispatch('setCurrentAction', null);
      }

      this.$store.dispatch('setActionTrayViewMode', 'project');
      this.$store.dispatch('setActionTrayVisibility', true);
    },
    countCompleted(group) {
      let count = 0;
      group.actions.forEach(action => {
        if (action.completed) {
          count++;
        }
      });
      return count;
    },
    getActionGroupStartDate(group) {
      if (!group.startDate) return '';
      return group.startDate.format(this.dateFormat);
    },
    getActionGroupEndDate(group) {
      if (!group.endDate) return '';
      return group.endDate.format(this.dateFormat);
    },
    getChartWidth(group) {
      if (!group.actions.length) return 0;
      return (100 * this.countCompleted(group)) / group.actions.length;
    },
    updateGroup(actionGroup) {
      if (actionGroup.id < 0) {
        this.$store.dispatch('createActionGroups', {
          actionGroups: [actionGroup],
          project_id: this.project_id,
        });
      } else {
        this.$store.dispatch('updateActionGroup', {
          actionGroup,
          project_id: this.project_id,
        });
      }
    },
    editGroup(id) {
      document.querySelector(`#panel-title-${id}`).click();
    },
    async deleteGroup(id) {
      try {
        if (id < 0) {
          let index;
          this.$store.getters.actions.forEach((group, ind) => {
            if (group.id === id) {
              index = ind;
            }
          });

          if (index || index === 0) {
            Vue.delete(this.$store.getters.actions, index);
          }

          return;
        }

        const options = {
          html: false,
          okText: 'Delete',
          cancelText: 'Cancel',
          customClass: 'acada-confirm',
          animation: 'fade',
        };

        const message = {
          title: 'Delete stage',
          body: 'Are you sure you want to delete this stage?',
        };

        await this.$dialog.confirm(message, options);
        this.$store.dispatch('deleteActionGroups', {
          actionGroups: [id],
          project_id: this.project_id,
        });
      } catch (e) {
        // they clicked cancel
      }
    },
    focus(event) {
      event.target.select();
    },
  },
  components: {
    Overflow,
    ActionCard,
  },
};
</script>

<style lang="scss" scoped>
@import '../styles/_styles.scss';

.card {
  border-color: #e2e5ec !important;
  border-radius: 0px !important;
  margin-bottom: 0px !important;
  border-left: none !important;
  border-right: none !important;
  border-bottom: none !important;
  margin-left: -25px;
  margin-right: -25px;

  &:last-child,
  &:last-child .card-header.collapsed,
  &:last-child .card-body {
    border-bottom-left-radius: 4px !important;
    border-bottom-right-radius: 4px !important;
  }
}

.card-header {
  background-color: white;
  padding: 25px 25px 18px !important;
  cursor: pointer;
  position: relative;
  border-color: #e2e5ec !important;
  border-radius: 0px !important;

  &:not(.collapsed) {
    background-color: #fbfbfc;

    .chevron {
      transform: rotate(180deg);
    }
  }

  &.collapsed {
    border-bottom-color: transparent !important;
    border-radius: 0px !important;
  }

  .chevron,
  .overflow {
    position: absolute;
    top: 21px;
    left: 33px;
    font-size: 28px;
    color: #acb6c1;
    transition: transform 0.5s;
    transform: rotate(360deg);
    user-select: none;
  }

  .overflow {
    left: auto;
    right: 25px;
    cursor: pointer;
    font-size: 18px;
  }

  h4 {
    color: #26292f;
    font-size: 16px;
    font-weight: 500;

    span {
      color: #acb6c1;
      font-weight: 400;
      font-size: 13px;

      i {
        font-size: 14px;
        position: relative;
        top: 2px;
        margin: 0 1px;
      }
    }
  }
}

.card-body {
  background-color: #fbfbfc;
  border-radius: 0px !important;
  padding: 0;

  .card-text {
    margin: 0;
  }
}

.flex-header {
  display: flex;
  flex-direction: column;
  margin-left: 60px;
  width: calc(100% - 50px);
  user-select: none;

  > div {
    display: flex;
    align-items: flex-end;
  }

  .panel-title {
    display: inline-block;
    margin-right: 9px;
    margin-bottom: 3px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
    max-width: calc(100% - 160px) !important;
    color: #26292f;
    font-size: 16px;
    font-weight: 500;
    background: transparent;
    border: none;
    border-bottom: 1px solid transparent;
    outline: none;
    padding: 0;
    padding-bottom: 3px;
    height: 24px;

    &:hover,
    &:focus,
    &:active {
      border-bottom-color: #b6bac4;
    }
  }

  .dates {
    color: #acb6c1;
    font-weight: 400;
    font-size: 13px;
    position: relative;
    margin-bottom: 6px;

    i {
      font-size: 14px;
      position: relative;
      top: 3px;
    }
  }

  .chart {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    width: 100%;

    span {
      font-weight: 500;
      font-size: 13px;
      color: #babcc5;
    }

    span.completed {
      color: #565b64;
    }
  }
}

.progress-bar {
  background-color: #e4e7ed;
  border-radius: 4px;
  height: 10px;
  width: calc(100% - 70px);
  max-width: 315px;
  margin-right: 10px;

  .progress {
    background-color: $light-green;
    transition: background 0.3s, width 0.3s;

    &.completed {
      background-color: $blue;
    }
  }
}

.add-subtask {
  padding-left: 90px;
  height: 62px;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-between;
  text-transform: uppercase;
  font-size: 13px;
  font-weight: 500;
  color: #626d79;
  cursor: pointer;
  padding-top: 3px;

  img {
    border: 1px dashed #acb6c1;
    border-radius: 20px;
    width: 30px;
    height: 30px;
    padding: 7px;
    margin-right: 12px;
    margin-top: -1px;
    opacity: 0.6;
    transition: opacity 0.3s;
  }

  .icons {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding-right: 13px;
    opacity: 0;
    transition: opacity 0.3s;

    .icon {
      margin-left: 4px;
      margin-right: 12px;
      border-radius: 15px;
      border: 1px dashed #ddd;
      width: 30px;
      height: 30px;
      background-repeat: no-repeat;
      background-position: center center;
      background-size: 16px 16px;
      background-image: url(~@/assets/icons/attachment.png);

      &.user {
        background-image: url(~@/assets/icons/user-grey.svg);
      }

      &.calendar {
        background-image: url(~@/assets/icons/date.svg);
      }
    }
  }

  &:hover {
    background: white;
    img {
      opacity: 1;
    }

    .icons {
      opacity: 1;
    }
  }
}

.list-move {
  transition: transform 0.5s;
}
</style>
